import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { BranchRow } from "@/modules/branches/domain/types";
import { branchesApi } from "@/modules/branches/presentation/api/client";
import type { StockPolicyListRow } from "@/modules/inventory/domain/types";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { toast } from "sonner";
import { CopyIcon, DownloadIcon, GaugeIcon, PlusIcon, UploadIcon } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import {
  inventoryStockPoliciesApi,
  formatStockPolicyApiError,
} from "../api/stock-policies-client";
import {
  useCopyGlobalPoliciesMutation,
  useImportStockPoliciesMutation,
  useStockPoliciesQuery,
} from "../api/stock-policies-queries";
import { PoItemCatalogPickerDialog } from "../ui/purchase/PoItemCatalogPickerDialog";
import type { PoCatalogPick } from "../ui/purchase/poCatalogTypes";
import {
  StockPoliciesFilter,
  stockPoliciesListParams,
  type StockPoliciesSearch,
} from "../ui/stock-policy/StockPoliciesFilter";
import { StockPoliciesTable } from "../ui/stock-policy/StockPoliciesTable";
import { StockPolicyEditDialog } from "../ui/stock-policy/StockPolicyEditDialog";
import { STOCK_POLICY_MENU_TITLE } from "../ui/stock-policy/stockPolicyLaoLabels";
import { TRANSFER_SECTION_STACK } from "../ui/transfers/transferCardLayout";

export function StockPoliciesPage() {
  const search = useSearch({
    from: "/app/inventory/stock-policies",
  }) as StockPoliciesSearch;

  const { data: branchesData } = useQuery({
    queryKey: ["branches", "list", { limit: 200, offset: 0 }],
    queryFn: () => branchesApi.list({ limit: 200, offset: 0 }),
  });
  const branches: BranchRow[] = branchesData?.data ?? [];
  const hub = branches.find((b) => b.isDistributionHub);

  const listParams = useMemo(
    () => stockPoliciesListParams(search, hub?.id),
    [search, hub?.id],
  );

  const query = useStockPoliciesQuery(listParams);
  const canUpdate = useActionPermission(["inventory:update"]);
  const copyMutation = useCopyGlobalPoliciesMutation();
  const importMutation = useImportStockPoliciesMutation();
  const importInputRef = useRef<HTMLInputElement>(null);

  const policyBranchId =
    (search.policyBranch ?? "__global__") === "__global__"
      ? null
      : (search.policyBranch ?? null);

  const [catalogOpen, setCatalogOpen] = useState(false);
  const [editRow, setEditRow] = useState<StockPolicyListRow | null>(null);
  const [pendingPick, setPendingPick] = useState<PoCatalogPick | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const openEdit = (row: StockPolicyListRow) => {
    setEditRow(row);
    setPendingPick(null);
    setEditOpen(true);
  };

  const onCatalogConfirm = (picks: PoCatalogPick[]) => {
    setCatalogOpen(false);
    const first = picks[0];
    if (!first) return;
    setPendingPick(first);
    setEditRow(null);
    setEditOpen(true);
    if (picks.length > 1) {
      // MVP: one at a time; user can re-open catalog
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ສາງ", to: "/app/inventory/lots" },
            { label: STOCK_POLICY_MENU_TITLE },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <GaugeIcon className="size-5" />
              {STOCK_POLICY_MENU_TITLE}
            </h2>
            <p className="text-muted-foreground">
              ຂັ້ນຕ່ຳ / ເປົ້າເຕີມສຕັອກ · Global ຫຼື override ຕາມສາຂາ
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                try {
                  const blob = await inventoryStockPoliciesApi.exportCsv(listParams);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `stock-policies-${new Date().toISOString().slice(0, 10)}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  toast.error("ສົ່ງອອກ CSV ບໍ່ສຳເລັດ");
                }
              }}
            >
              <DownloadIcon className="size-4" />
              CSV
            </Button>
            {canUpdate ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => importInputRef.current?.click()}
                  disabled={importMutation.isPending}
                >
                  <UploadIcon className="size-4" />
                  Import
                </Button>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (!file) return;
                    try {
                      const csv = await file.text();
                      const res = await importMutation.mutateAsync(csv);
                      toast.success(`Import ${res.updated} ລາຍການ`);
                    } catch (err) {
                      toast.error(formatStockPolicyApiError(err));
                    }
                  }}
                />
                {policyBranchId ? (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={copyMutation.isPending}
                    onClick={async () => {
                      try {
                        const res = await copyMutation.mutateAsync({
                          branchId: policyBranchId,
                          belowMinOnly: search.belowMinOnly === "true",
                        });
                        toast.success(
                          `ຄັດລອກ global → ສາຂາ ${res.copied} ລາຍການ`,
                        );
                      } catch (err) {
                        toast.error(formatStockPolicyApiError(err));
                      }
                    }}
                  >
                    <CopyIcon className="size-4" />
                    ຄັດລອກ global
                  </Button>
                ) : null}
                <Button type="button" onClick={() => setCatalogOpen(true)}>
                  <PlusIcon className="size-4" />
                  ເພີ່ມລາຍການ
                </Button>
              </>
            ) : null}
          </div>
        </div>

        <div className={TRANSFER_SECTION_STACK}>
          <StockPoliciesFilter />

          <QueryState result={query} title="ກຳລັງໂຫຼດເກັນໄຂ">
            <StockPoliciesTable
              rows={query.data?.items ?? []}
              canUpdate={!!canUpdate}
              onEdit={openEdit}
            />
            {query.data && query.data.total > (listParams.limit ?? 50) ? (
              <p className="text-muted-foreground text-sm">
                ສະແດງ {query.data.items.length.toLocaleString("lo-LA")} /{" "}
                {query.data.total.toLocaleString("lo-LA")} ລາຍການ
              </p>
            ) : null}
          </QueryState>
        </div>

        <PoItemCatalogPickerDialog
          open={catalogOpen}
          onOpenChange={setCatalogOpen}
          mode="single"
          onConfirm={(picks) => onCatalogConfirm(picks)}
        />

        <StockPolicyEditDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          row={editRow}
          pick={pendingPick}
          policyBranchId={policyBranchId}
        />
      </Main>
    </>
  );
}
